/**
 * @file StockLevelsResults.tsx
 * @description Список остатков в работе: выбор строк, «⋯», размещение и
 * групповая печать этикеток (phase19.24 этап 1, шаг 4; печать — phase19.22,
 * W17-9c; ui-design §2; таблица и компактный список по SC59 — phase19.31).
 *
 * Панель вкладки отвечает за данные и правило выбора, этот компонент — за
 * работу с уже отобранными строками: галочки, «Выбрать все», массовое
 * размещение, печать этикеток, быстрая перестановка партии и переходы.
 *
 * После любой мутации (размещение, печать) список перечитывается с сервера:
 * адрес и признак «этикетки напечатаны» знает только сервер, и напечатанные
 * партии сами уходят из отбора «Без этикеток».
 */

import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { PLACEMENT_TEXT } from "./stock-placement-texts";
import { PlacementSheet } from "./PlacementSheet";
import { StockLevelsList } from "./StockLevelsList";
import { StockLevelsTable } from "./StockLevelsTable";
import { StockSelectionBar } from "./StockSelectionBar";
import { isPrintable, stockPrintItems, totalLabels } from "./stock-label-print";
import { isPlaceable, stockRowActions } from "./stock-row-actions";
import type { StockSelectionApi } from "./use-stock-selection";
import { useStockLabelQty } from "./use-stock-label-qty";
import { LabelPrintDialog } from "../labels/LabelPrintDialog";
import { Checkbox } from "../../ui/checkbox";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { useNotify } from "../../ui/NotifyProvider";
import { MoveLotBinDialog } from "../MoveLotBinDialog";
import type { PlacementResult } from "../../../lib/api-transfers";
import type { StockLevelDto } from "../../../lib/api-wms";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";

interface Props {
  items: StockLevelDto[];
  /** Отбор «Без места» или по этикеткам — у строк появляются галочки. */
  selectable: boolean;
  selection: StockSelectionApi;
  /** Правило выбора строки в отборе по этикеткам (см. панель). */
  selectState?: (level: StockLevelDto) => { can: boolean; hint: string };
  /** Отбор «Без места» — доступно «Разместить». */
  placing: boolean;
  /** Отбор «Без этикеток» / «Устарели» — доступна «Печать». */
  labelsMode: boolean;
  /** Склад фильтра — калибровка листов; пусто — печать только из одного склада. */
  printWarehouseId: string;
  /** SC59: колонка/подпись «Склад» — только при нескольких складах без выбора. */
  showWarehouse: boolean;
  onChanged: () => void;
}

export function StockLevelsResults({
  items,
  selectable,
  selection,
  selectState,
  placing,
  labelsMode,
  printWarehouseId,
  showWarehouse,
  onChanged,
}: Props) {
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const shopPath = `/shop/${slug}`;
  const wmsPath = `${shopPath}/wms`;
  const transferHref = (id: string) => `${wmsPath}/transfers/${id}`;

  const [moving, setMoving] = useState<StockLevelDto | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  // Печать прошла — отбор перечитываем после закрытия шторки, не под итогом.
  const [printed, setPrinted] = useState(false);
  const labelQty = useStockLabelQty();

  const toPlace = selection.selected.filter(isPlaceable);
  const toPrint = labelsMode ? selection.selected.filter(isPrintable) : [];
  // Печать идёт по калибровке листов ОДНОГО склада: в отборе «все склады»
  // партии разных складов вместе не печатаем — просим выбрать склад.
  const printWarehouses = new Set(toPrint.map((level) => level.warehouseId));
  const printWarehouse = printWarehouseId || (printWarehouses.size === 1 ? toPrint[0]!.warehouseId : "");
  const printHint =
    toPrint.length > 0 && !printWarehouse ? "Выберите склад в фильтре: этикетки печатаются по листам одного склада" : null;
  const allChecked = selection.selected.length > 0 && selection.selected.length === items.filter((l) => (selectState ? selectState(l).can : isPlaceable(l))).length;

  function handlePlaced(result: PlacementResult, placedCount: number) {
    const transfer = result.transfer;
    if (transfer) {
      notify.success(PLACEMENT_TEXT.placedMany(placedCount, transfer.transferNumber), {
        action: { label: PLACEMENT_TEXT.openDocument, onClick: () => navigate(transferHref(transfer.id)) },
      });
    }
    if (result.skipped.length === 0) {
      setSheetOpen(false);
      selection.clear();
    }
    onChanged();
  }

  const rowView = {
    items,
    selectable,
    isSelected: selection.isSelected,
    onToggle: selection.toggle,
    selectState,
    labelsMode,
    qtyOf: labelQty.qtyOf,
    onQty: labelQty.setQty,
    showWarehouse,
    actions: (level: StockLevelDto) => (
      <RowActionsMenu
        size="compact"
        title={PLACEMENT_TEXT.rowMenuTitle}
        ariaLabel={`${PLACEMENT_TEXT.rowMenuTitle}: ${level.lot?.batchNumber ?? level.sku?.code ?? ""}`}
        actions={stockRowActions(level, { shopPath, wmsPath }, { onMoveBin: setMoving, onNavigate: (href) => navigate(href) })}
      />
    ),
  };

  return (
    <>
      {selectable ? (
        <label className="flex min-h-[var(--control-h,3rem)] cursor-pointer items-center gap-2 self-start rounded-lg pr-3">
          <span className="inline-flex size-[var(--control-h,3rem)] items-center justify-center lg:size-11">
            <Checkbox
              checked={allChecked}
              onCheckedChange={() => (allChecked ? selection.clear() : selection.selectAll())}
              aria-label="Выбрать все строки отбора"
              className="size-5"
            />
          </span>
          <span className="text-sm font-medium">Выбрать все</span>
        </label>
      ) : null}

      {/* Одна раскладка в DOM: до 1024 — компактный список, от 1024 — таблица. */}
      {compact ? <StockLevelsList {...rowView} /> : <StockLevelsTable {...rowView} />}

      <StockSelectionBar
        count={selection.selected.length}
        onPlace={placing && toPlace.length > 0 ? () => setSheetOpen(true) : undefined}
        onClear={selection.clear}
        onPrint={labelsMode ? () => setPrintOpen(true) : undefined}
        printLabels={totalLabels(toPrint, labelQty.qtyOf)}
        printHint={printHint}
      />

      <PlacementSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        rows={toPlace}
        onPlaced={handlePlaced}
        onFailed={onChanged}
        transferHref={transferHref}
      />

      {labelsMode ? (
        <LabelPrintDialog
          open={printOpen}
          onOpenChange={(open) => {
            setPrintOpen(open);
            if (open || !printed) return;
            setPrinted(false);
            selection.clear();
            labelQty.reset();
            onChanged();
          }}
          warehouseId={printWarehouse}
          mode="stock"
          subtitle={`Остатки · выбрано ${toPrint.length}`}
          items={stockPrintItems(toPrint, labelQty.qtyOf)}
          onPrinted={() => setPrinted(true)}
        />
      ) : null}

      {moving?.lot ? (
        <MoveLotBinDialog
          lotId={moving.lot.id}
          batchNumber={moving.lot.batchNumber}
          warehouseId={moving.warehouseId}
          currentBinCode={moving.lot.bin?.code ?? null}
          onMoved={onChanged}
          onClose={() => setMoving(null)}
        />
      ) : null}
    </>
  );
}
